import * as React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"

// Pulse traffic dashboard, ported from the standalone Pulse app and restyled
// with this app's design tokens. Data comes from /admin/analytics/data/* via
// raw fetch() (auth is the admin session cookie), scoped by brand.

export type Period = "today" | "7d" | "30d" | "90d" | "12m"

type SummaryData = {
  pageviews: number
  unique_visitors: number
  avg_session_seconds: number
  custom_events: number
  bounce_rate: number
  pageviews_delta: number
  visitors_delta: number
  avg_session_delta: number
  events_delta: number
  bounce_rate_delta: number
}

type SeriesPoint = { date: string; unique_visitors: number; pageviews: number }
type PageRow = { page: string; pageviews: number; unique_visitors: number; pct_of_total: number }
type SourceRow = { referrer_source: string; referrer: string; visitors: number; pct: number }
type CountryRow = { country_code: string; country_name: string; visitors: number; pct: number }
type DeviceRow = { device_type: string; visitors: number; pct: number }
type BrowserRow = { browser: string; visitors: number; pct: number }
type RealtimeData = { active_visitors: number; active_pages: { page: string; count: number }[] }
type AudienceSide = { users: number; pageviews: number; pct_of_views: number }
type AudienceData = {
  total: { users: number; pageviews: number }
  crawlers: AudienceSide & { agents: { name: string; pageviews: number }[] }
  humans: AudienceSide
}
type FunnelStage = { key: "visitors" | "intent" | "acted"; value: number }
type FunnelAction = { name: string; count: number; visitors: number }
type FunnelData = {
  stages: FunnelStage[]
  actions: FunnelAction[]
  records: Record<string, number>
  storing_enquiries: boolean
}

// Tracking here is cookieless — a visitor is a same-day hash of address and
// browser — which buys the site a life without a consent banner and costs it
// the ability to recognise the same person twice. A day is therefore the
// largest window the identifier can honestly fill: within one it counts
// browsers, across several it just adds those daily counts up, so a visitor who
// came back on Tuesday appears again. The headline reports the day; the
// breakdowns below it are period totals, which rank sources and pages correctly
// but are not a count of people.
function VisitorCountNote() {
  return (
    <p className="mt-3 text-sm text-ink-muted">
      A visitor is one browser on one day — no cookies are set, so a laptop and a phone count
      separately. The breakdowns further down add those daily figures up across the period: good
      for comparing pages and sources, not a headcount. Crawlers are counted separately and left
      out of every figure here — see “Who this traffic was” below — and any address on the
      “traffic not counted” list at the foot of this page is not recorded at all.
    </p>
  )
}

// Sum of the daily figures over the period divided by the days in it — which is
// exactly the mean of the chart below, because a visitor identifier belongs to
// one day and so can never be counted in two of them. Dividing by the series
// length rather than by the period's nominal length is what keeps the headline
// and the chart agreeing on their edges.
function averagePerDay(summary: SummaryData, series: SeriesPoint[]): number {
  if (series.length === 0) return summary.unique_visitors
  return summary.unique_visitors / series.length
}

const TEAL = "#1B4A52"
const SAGE = "#7BA696"

function formatDuration(secs: number): string {
  const s = Math.round(secs)
  if (s === 0) return "0s"
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

// One decimal only while the average is small enough for the decimal to carry
// information: "2.4 a day" says something "2 a day" does not, where "17.3"
// says nothing "17" does not.
function formatPerDay(value: number): string {
  return value < 10 ? value.toFixed(1) : Math.round(value).toLocaleString()
}

function busiestDayNote(series: SeriesPoint[]): string | undefined {
  if (series.length === 0) return undefined

  const busiest = series.reduce((a, b) => (b.unique_visitors > a.unique_visitors ? b : a))
  if (busiest.unique_visitors === 0) return undefined

  const day = new Date(busiest.date.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  })
  return `${busiest.unique_visitors.toLocaleString()} on the busiest day (${day})`
}

function formatXAxisDate(date: string, period: Period): string {
  if (period === "today") return date.slice(11, 16)
  const d = new Date(date + "T00:00:00Z")
  return d.toLocaleDateString("en-GB", { month: "short", day: "numeric", timeZone: "UTC" })
}

const PERIODS: { value: Period; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "12m", label: "12 months" },
]

function PeriodSelector({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          className="toggle-button"
          aria-pressed={period === p.value}
        >
          {p.label}
        </button>
      ))}
    </div>
  )
}

function RealtimeStrip({ data }: { data: RealtimeData | null }) {
  if (!data) return null

  return (
    <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg border border-hairline bg-surface px-5 py-3">
      <span className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
        </span>
        <span className="text-2xl font-light text-ink-display">{data.active_visitors}</span>
        <span className="text-sm text-ink-muted">online now</span>
      </span>
      <div className="h-6 w-px shrink-0 bg-hairline" />
      {data.active_pages.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {data.active_pages.map((ap) => (
            <span
              key={ap.page}
              className="rounded-md bg-accent-faded px-2 py-0.5 font-mono text-xs text-accent"
            >
              {ap.page} · {ap.count}
            </span>
          ))}
        </div>
      ) : (
        <span className="text-xs text-ink-muted">No active pages right now</span>
      )}
    </div>
  )
}

function DeltaChip({ delta, invert = false }: { delta: number | null; invert?: boolean }) {
  if (delta === null || delta === 0) {
    return <span className="text-sm text-ink-muted">no change</span>
  }

  const isGood = invert ? delta < 0 : delta > 0
  const arrow = delta > 0 ? "↑" : "↓"

  return (
    <span className={isGood ? "text-sm text-green-600" : "text-sm text-red-500"}>
      {arrow} {Math.abs(delta).toFixed(1)}%
    </span>
  )
}

function StatCard({
  label,
  value,
  delta,
  note,
  invertDelta = false,
}: {
  label: string
  value: string
  delta: number | null
  note?: string
  invertDelta?: boolean
}) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-5">
      <p className="text-sm text-ink-muted">{label}</p>
      <p className="mt-1 text-3xl font-light text-ink-display">{value}</p>
      <div className="mt-1">
        <DeltaChip delta={delta} invert={invertDelta} />
      </div>
      {note && <p className="mt-1 text-xs text-ink-muted">{note}</p>}
    </div>
  )
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { name: string; value: number; color: string }[]
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-hairline bg-surface px-3 py-2 text-xs text-ink-body">
      <p className="mb-1 text-ink-muted">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color }}>
          {entry.name}: {entry.value.toLocaleString()}
        </p>
      ))}
    </div>
  )
}

function VisitorsChart({ series, period }: { series: SeriesPoint[]; period: Period }) {
  const formatted = series.map((pt) => ({
    ...pt,
    label: formatXAxisDate(pt.date, period),
  }))

  return (
    <div className="mt-6 rounded-lg border border-hairline bg-surface p-5">
      <h2 className="mb-4">Visitors over time</h2>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={formatted} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="gradVisitors" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={TEAL} stopOpacity={0.3} />
              <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradPageviews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={SAGE} stopOpacity={0.4} />
              <stop offset="95%" stopColor={SAGE} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-hairline)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: "var(--color-ink-muted)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fill: "var(--color-ink-muted)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="pageviews"
            name="Pageviews"
            stroke={SAGE}
            strokeWidth={2}
            fill="url(#gradPageviews)"
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="unique_visitors"
            name="Visitors"
            stroke={TEAL}
            strokeWidth={2}
            fill="url(#gradVisitors)"
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function ListRow({
  rank,
  name,
  value,
  maxValue,
}: {
  rank?: number
  name: React.ReactNode
  value: number
  maxValue: number
}) {
  return (
    <div className="relative flex items-center gap-3 border-t border-hairline px-4 py-2.5">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 bg-accent-faded"
        style={{ width: maxValue > 0 ? `${(value / maxValue) * 100}%` : "0%" }}
      />
      {rank !== undefined && (
        <span className="relative w-4 shrink-0 text-right font-mono text-xs text-ink-muted">
          {rank}
        </span>
      )}
      <span className="relative min-w-0 flex-1 truncate text-sm text-ink-body">{name}</span>
      <span className="relative shrink-0 font-mono text-sm text-ink-muted">
        {value.toLocaleString()}
      </span>
    </div>
  )
}

function ListCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-hairline bg-surface">
      <div className="px-4 py-3">
        <h2>{title}</h2>
      </div>
      {children}
    </div>
  )
}

function NoData() {
  return <p className="border-t border-hairline px-4 py-6 text-sm text-ink-muted">No data yet.</p>
}

function SourceBadge({ source }: { source: string }) {
  return (
    <span
      className={
        source === "organic"
          ? "mr-1.5 rounded bg-accent-faded px-1.5 py-0.5 text-xs font-medium text-accent"
          : "mr-1.5 rounded bg-page px-1.5 py-0.5 text-xs font-medium text-ink-muted"
      }
    >
      {source}
    </span>
  )
}

function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join("")
}

const DEVICE_COLORS: Record<string, string> = {
  desktop: TEAL,
  mobile: SAGE,
  tablet: "#D97706",
}

function DonutSegment({
  cx, cy, outerR, innerR, startAngle, endAngle, color,
}: {
  cx: number; cy: number; outerR: number; innerR: number
  startAngle: number; endAngle: number; color: string
}) {
  if (endAngle - startAngle < 0.5) return null
  const toRad = (deg: number) => ((deg - 90) * Math.PI) / 180
  const x1o = cx + outerR * Math.cos(toRad(startAngle))
  const y1o = cy + outerR * Math.sin(toRad(startAngle))
  const x2o = cx + outerR * Math.cos(toRad(endAngle))
  const y2o = cy + outerR * Math.sin(toRad(endAngle))
  const x1i = cx + innerR * Math.cos(toRad(endAngle))
  const y1i = cy + innerR * Math.sin(toRad(endAngle))
  const x2i = cx + innerR * Math.cos(toRad(startAngle))
  const y2i = cy + innerR * Math.sin(toRad(startAngle))
  const largeArc = endAngle - startAngle > 180 ? 1 : 0
  const d = [
    `M ${x1o} ${y1o}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2o} ${y2o}`,
    `L ${x1i} ${y1i}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2i} ${y2i}`,
    "Z",
  ].join(" ")
  return <path d={d} fill={color} />
}

function DonutChart({ devices }: { devices: DeviceRow[] }) {
  const total = devices.reduce((s, d) => s + d.visitors, 0)
  let angle = 0
  const segments = devices.map((d) => {
    const sweep = total > 0 ? (d.visitors / total) * 360 : 0
    const start = angle
    angle += sweep
    return { ...d, start, end: angle }
  })

  return (
    <ListCard title="Devices">
      <div className="flex flex-col items-center border-t border-hairline py-4">
        {total === 0 ? (
          <p className="py-8 text-sm text-ink-muted">No data yet.</p>
        ) : (
          <>
            <svg width="120" height="120" viewBox="0 0 120 120">
              {segments.map((seg) => (
                <DonutSegment
                  key={seg.device_type}
                  cx={60} cy={60} outerR={50} innerR={30}
                  startAngle={seg.start} endAngle={seg.end}
                  color={DEVICE_COLORS[seg.device_type] ?? "var(--color-ink-muted)"}
                />
              ))}
              <circle cx="60" cy="60" r="28" fill="var(--color-surface)" />
            </svg>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {devices.map((d) => (
                <div key={d.device_type} className="flex items-center gap-1.5 text-xs">
                  <span
                    className="h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: DEVICE_COLORS[d.device_type] ?? "var(--color-ink-muted)" }}
                  />
                  <span className="text-ink-muted">
                    {d.device_type} {d.pct.toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </ListCard>
  )
}

const STAGE_LABELS: Record<FunnelStage["key"], string> = {
  visitors: "Unique visitors",
  intent: "Reached a booking/contact page",
  acted: "Took action",
}

const ACTION_LABELS: Record<string, string> = {
  enquiry: "Therapy enquiry sent",
  consultation_booked: "Free consultation booked",
  callback_request: "Callback requested",
  viewing_request: "Room viewing requested",
  newsletter_signup: "Newsletter signup",
  call_click: "Phone number tapped",
  email_click: "Email address clicked",
}

const RECORD_LABELS: Record<string, string> = {
  consultations: "Consultation bookings",
  therapy_enquiries: "Therapy enquiries",
  callback_requests: "Callback requests",
  newsletter_signups: "Newsletter signups",
  viewing_requests: "Room viewing requests",
  contact_submissions: "Contact messages",
}

function pctOf(value: number, base: number): string {
  if (base <= 0) return "—"
  return `${((value / base) * 100).toFixed(1)}%`
}

function AudienceCard({ audience }: { audience: AudienceData }) {
  const { total, crawlers, humans } = audience
  const bar = (pct: number) => Math.max(pct, pct > 0 ? 2 : 0)

  return (
    <div className="mt-6 rounded-lg border border-hairline bg-surface">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-4">
        <h2>Who this traffic was</h2>
        <span className="text-sm text-ink-muted">
          {total.pageviews > 0
            ? `${crawlers.pct_of_views.toFixed(1)}% of pageviews were crawlers`
            : "No traffic in this period yet"}
        </span>
      </div>

      <div className="grid gap-6 p-5 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Total users", value: total.users, views: total.pageviews },
              { label: "Crawlers", value: crawlers.users, views: crawlers.pageviews },
              { label: "Human users", value: humans.users, views: humans.pageviews },
            ].map((tile) => (
              <div key={tile.label} className="rounded-md border border-hairline p-3">
                <p className="text-ink-muted">{tile.label}</p>
                <p className="mt-1 text-2xl text-ink-display">{tile.value.toLocaleString()}</p>
                <p className="text-ink-muted">{tile.views.toLocaleString()} pageviews</p>
              </div>
            ))}
          </div>

          <div>
            <div className="flex h-3 overflow-hidden rounded-full bg-accent-faded/40">
              <div
                className="bg-accent"
                style={{ width: `${bar(humans.pct_of_views)}%` }}
                title={`Human ${humans.pct_of_views}%`}
              />
              <div
                className="bg-ink-muted/40"
                style={{ width: `${bar(crawlers.pct_of_views)}%` }}
                title={`Crawler ${crawlers.pct_of_views}%`}
              />
            </div>
            <p className="mt-2 text-ink-muted">
              Every other figure on this page counts people only. A crawler is identified the
              same way a browser is, so &ldquo;users&rdquo; means the same thing on both sides.
            </p>
          </div>
        </div>

        <div>
          <h3>Crawlers seen</h3>
          {crawlers.agents.length === 0 ? (
            <p className="mt-2 text-ink-muted">No crawler ran the tracking script in this period.</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {crawlers.agents.map((agent) => (
                <li key={agent.name} className="flex items-baseline justify-between gap-3">
                  <span className="truncate">{agent.name}</span>
                  <span className="shrink-0 text-ink-muted">
                    {agent.pageviews.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-ink-muted">
            Only crawlers that run JavaScript appear here; the ones that read the HTML and
            leave never reach the tracker at all.
          </p>
        </div>
      </div>
    </div>
  )
}

function FunnelCard({ funnel }: { funnel: FunnelData }) {
  const visitors = funnel.stages.find((s) => s.key === "visitors")?.value ?? 0
  const acted = funnel.stages.find((s) => s.key === "acted")?.value ?? 0
  const maxActionCount = funnel.actions[0]?.count ?? 0
  const records = Object.entries(funnel.records).filter(([, v]) => v >= 0)

  return (
    <div className="mt-6 rounded-lg border border-hairline bg-surface">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-4">
        <h2>Enquiry funnel</h2>
        <span className="text-sm text-ink-muted">
          {visitors > 0
            ? `${pctOf(acted, visitors)} of visitors took action`
            : "No visitors in this period yet"}
        </span>
      </div>

      <div className="grid gap-6 p-5 lg:grid-cols-2">
        {/* Stages */}
        <div className="flex flex-col gap-3">
          {funnel.stages.map((stage, i) => {
            const width = visitors > 0 ? Math.max((stage.value / visitors) * 100, stage.value > 0 ? 2 : 0) : 0
            const prev = funnel.stages[i - 1]?.value ?? 0
            return (
              <div key={stage.key}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm text-ink-body">{STAGE_LABELS[stage.key]}</span>
                  <span className="font-mono text-sm text-ink-muted">
                    {stage.value.toLocaleString()}
                    {i > 0 && <span className="ml-2 text-xs">({pctOf(stage.value, prev)})</span>}
                  </span>
                </div>
                <div className="mt-1 h-5 overflow-hidden rounded bg-accent-faded/40">
                  <div
                    className="h-full rounded bg-accent transition-all"
                    style={{ width: `${width}%`, opacity: 1 - i * 0.25 }}
                  />
                </div>
              </div>
            )
          })}
          <p className="mt-1 text-xs text-ink-muted">
            "Took action" counts visitors who sent a form or tapped a phone number / email
            address. Ad-blockers can hide some of these, so treat the database column as the
            ground truth for forms — phone taps only exist here.
          </p>
        </div>

        {/* Actions + database records */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-ink-muted">Actions on the site</p>
            {funnel.actions.length === 0 ? (
              <p className="mt-2 text-sm text-ink-muted">None recorded yet.</p>
            ) : (
              <div className="mt-2">
                {funnel.actions.map((a) => (
                  <ListRow
                    key={a.name}
                    name={ACTION_LABELS[a.name] ?? a.name}
                    value={a.count}
                    maxValue={maxActionCount}
                  />
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-sm text-ink-muted">In the database (same period)</p>
            {!funnel.storing_enquiries && (
              <p className="mt-2 text-sm text-ink-muted">
                Enquiry, contact, callback and viewing forms go straight to the inbox
                and are not stored, so they are not counted here.
              </p>
            )}
            <div className="mt-2">
              {records.map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-center justify-between border-t border-hairline px-4 py-2.5"
                >
                  <span className="text-sm text-ink-body">{RECORD_LABELS[key] ?? key}</span>
                  <span className="font-mono text-sm text-ink-muted">{value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SkeletonBlock({ height = 80 }: { height?: number }) {
  return (
    <div
      className="animate-pulse rounded-lg border border-hairline bg-surface"
      style={{ height }}
    />
  )
}

export function PulseDashboard({ brand }: { brand: "gisela" | "sanctuary" | null }) {
  const [period, setPeriod] = useState<Period>("30d")
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState<SummaryData | null>(null)
  const [series, setSeries] = useState<SeriesPoint[]>([])
  const [pages, setPages] = useState<PageRow[]>([])
  const [sources, setSources] = useState<SourceRow[]>([])
  const [countries, setCountries] = useState<CountryRow[]>([])
  const [devices, setDevices] = useState<DeviceRow[]>([])
  const [browsers, setBrowsers] = useState<BrowserRow[]>([])
  const [funnel, setFunnel] = useState<FunnelData | null>(null)
  const [audience, setAudience] = useState<AudienceData | null>(null)
  const [realtime, setRealtime] = useState<RealtimeData | null>(null)

  const query = `brand=${brand ?? ""}&period=${period}`

  const fetchDashboard = useCallback(async (q: string) => {
    setLoading(true)
    try {
      const [sumRes, serRes, pgRes, srcRes, ctryRes, devRes, funRes, audRes] = await Promise.all([
        fetch(`/admin/analytics/data/summary?${q}`),
        fetch(`/admin/analytics/data/series?${q}`),
        fetch(`/admin/analytics/data/pages?${q}&limit=8`),
        fetch(`/admin/analytics/data/sources?${q}`),
        fetch(`/admin/analytics/data/countries?${q}`),
        fetch(`/admin/analytics/data/devices?${q}`),
        fetch(`/admin/analytics/data/funnel?${q}`),
        fetch(`/admin/analytics/data/audience?${q}`),
      ])
      const [sum, ser, pg, src, ctry, dev, fun, aud] = await Promise.all([
        sumRes.json(), serRes.json(), pgRes.json(),
        srcRes.json(), ctryRes.json(), devRes.json(), funRes.json(), audRes.json(),
      ])
      setSummary(sum)
      setSeries(ser.series ?? [])
      setPages(pg.pages ?? [])
      setSources(src.sources ?? [])
      setCountries(ctry.countries ?? [])
      setDevices(dev.devices ?? [])
      setBrowsers(dev.browsers ?? [])
      setFunnel(fun.stages ? fun : null)
      setAudience(aud.total ? aud : null)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboard(query)
  }, [query, fetchDashboard])

  const fetchRealtime = useCallback(async (b: string) => {
    try {
      const res = await fetch(`/admin/analytics/data/realtime?brand=${b}`)
      setRealtime(await res.json())
    } catch (e) {
      console.error(e)
    }
  }, [])

  useEffect(() => {
    const b = brand ?? ""
    fetchRealtime(b)
    const id = setInterval(() => fetchRealtime(b), 30_000)
    return () => clearInterval(id)
  }, [brand, fetchRealtime])

  const maxPageviews = pages[0]?.pageviews ?? 0
  const maxSourceVisitors = sources[0]?.visitors ?? 0
  const maxCountryVisitors = countries[0]?.visitors ?? 0
  const maxBrowserVisitors = browsers[0]?.visitors ?? 0

  return (
    <>
      <div className="mt-6">
        <PeriodSelector period={period} onChange={setPeriod} />
      </div>

      <RealtimeStrip data={realtime} />

      {loading || !summary ? (
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonBlock key={i} height={120} />
          ))}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {period === "today" ? (
            <StatCard
              label="Unique visitors today"
              value={summary.unique_visitors.toLocaleString()}
              delta={summary.visitors_delta}
            />
          ) : (
            <StatCard
              label="Unique visitors a day"
              value={formatPerDay(averagePerDay(summary, series))}
              delta={summary.visitors_delta}
              note={busiestDayNote(series)}
            />
          )}
          <StatCard
            label="Total pageviews"
            value={summary.pageviews.toLocaleString()}
            delta={summary.pageviews_delta}
          />
          <StatCard
            label="Bounce rate"
            value={`${(summary.bounce_rate * 100).toFixed(1)}%`}
            delta={summary.bounce_rate_delta}
            invertDelta
          />
          <StatCard
            label="Avg visit duration"
            value={formatDuration(summary.avg_session_seconds)}
            delta={summary.avg_session_delta}
          />
        </div>
      )}

      <VisitorCountNote />

      {loading || series.length === 0 ? (
        <div className="mt-6">
          <SkeletonBlock height={280} />
        </div>
      ) : (
        <VisitorsChart series={series} period={period} />
      )}

      {loading || !audience ? (
        <div className="mt-6">
          <SkeletonBlock height={240} />
        </div>
      ) : (
        <AudienceCard audience={audience} />
      )}

      {loading || !funnel ? (
        <div className="mt-6">
          <SkeletonBlock height={260} />
        </div>
      ) : (
        <FunnelCard funnel={funnel} />
      )}

      {loading ? (
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <SkeletonBlock key={i} height={300} />
          ))}
        </div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <ListCard title="Top pages">
            {pages.length === 0 ? (
              <NoData />
            ) : (
              pages.map((row, i) => (
                <ListRow
                  key={row.page}
                  rank={i + 1}
                  name={<span className="font-mono text-xs">{row.page}</span>}
                  value={row.pageviews}
                  maxValue={maxPageviews}
                />
              ))
            )}
          </ListCard>

          <ListCard title="Traffic sources">
            {sources.length === 0 ? (
              <NoData />
            ) : (
              sources.map((row, i) => (
                <ListRow
                  key={`${row.referrer_source}-${row.referrer}-${i}`}
                  rank={i + 1}
                  name={
                    <span className="flex items-center gap-1">
                      <SourceBadge source={row.referrer_source} />
                      <span className="truncate">{row.referrer || "Direct"}</span>
                    </span>
                  }
                  value={row.visitors}
                  maxValue={maxSourceVisitors}
                />
              ))
            )}
          </ListCard>

          <ListCard title="Countries">
            {countries.length === 0 ? (
              <NoData />
            ) : (
              countries.map((row, i) => (
                <ListRow
                  key={row.country_code}
                  rank={i + 1}
                  name={
                    <span>
                      {countryFlag(row.country_code)} {row.country_name}
                    </span>
                  }
                  value={row.visitors}
                  maxValue={maxCountryVisitors}
                />
              ))
            )}
          </ListCard>
        </div>
      )}

      {loading ? (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <SkeletonBlock height={280} />
          <SkeletonBlock height={280} />
        </div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <DonutChart devices={devices} />
          <ListCard title="Browsers">
            {browsers.length === 0 ? (
              <NoData />
            ) : (
              browsers.map((row) => (
                <ListRow
                  key={row.browser || "unknown"}
                  name={row.browser || "Unknown"}
                  value={row.visitors}
                  maxValue={maxBrowserVisitors}
                />
              ))
            )}
          </ListCard>
        </div>
      )}
    </>
  )
}
